import { type FC, useMemo } from 'react';
import { useTranslation } from 'react-i18next';

import { ArrowPathIcon, PencilSquareIcon } from '@heroicons/react/24/outline';
import { useFetcher } from '@remix-run/react';

import { useEnsureCsrfToken } from '~/context/CsrfContext';
import useToast, { type ToastData, ToastStatus } from '~/hooks/useToast';
import type { CommunityPost } from '~/interfaces/community';
import { NectarPageBlock } from '~/utils/nectarPageBlock';
import { withNectarTrackingParams } from '~/utils/nectarTracking';

import { ActionMenu } from '../../ui/ActionMenu';
import { IconButton } from '../../ui/IconButton';

import { useSpaceAccess } from '../../../_hooks/useSpaceAccess';
import { usePostComposer } from '../../PostComposer';
import { actionIconClass } from '../constants';

interface RepostMenuProps {
  post: CommunityPost;
}

type RepostResponse = {
  id?: string;
  toast?: {
    status: 'success' | 'error';
    message: string;
  };
};

export const RepostMenu: FC<RepostMenuProps> = ({ post }) => {
  const { t } = useTranslation();
  const ensureCsrfToken = useEnsureCsrfToken();
  const isDisabled = post.attachable_type === 'CommunityPost';
  const { openComposer } = usePostComposer();
  const { canPostIn, canPostAnywhere, feedFirstEnabled } = useSpaceAccess();
  const fetcher = useFetcher<RepostResponse>();

  const toastData: ToastData | undefined = useMemo(() => {
    if (fetcher.state !== 'idle' || !fetcher.data?.toast) return undefined;

    return {
      ...fetcher.data.toast,
      status: fetcher.data.toast.status === 'success' ? ToastStatus.SUCCESS : ToastStatus.ERROR,
    };
  }, [fetcher.state, fetcher.data]);
  useToast(toastData);

  const canRepost = post.space ? canPostIn(post.space) : feedFirstEnabled && canPostIn(null);

  const handleRepost = async () => {
    if (!canRepost) return;

    const fields: Record<string, string> = {
      attachable_type: 'CommunityPost',
      attachable_id: post.id,
      composer_mode: 'repost',
      csrf: await ensureCsrfToken(),
    };
    if (post.space?.slug) fields.space_slug = post.space.slug;

    fetcher.submit(withNectarTrackingParams(fields, NectarPageBlock.CommunityPostForm), {
      method: 'POST',
      action: '/community_post',
    });
  };

  const handleQuotePost = () => {
    const channelSlug = post.space && canPostIn(post.space) ? post.space.slug : undefined;
    openComposer({
      // Pinning the composer to the post's channel hides the space selector, so
      // only do it when the viewer can actually post there; otherwise let them
      // pick Feed or a channel that accepts their posts.
      defaultSpaceSlug: channelSlug,
      quotedPost: post,
    });
  };

  if (isDisabled) {
    return (
      <IconButton
        aria-label={t('community.repost.repost', 'Repost')}
        disabled
        className="opacity-40 cursor-not-allowed hover:bg-transparent"
      >
        <ArrowPathIcon className={actionIconClass} />
      </IconButton>
    );
  }

  return (
    <ActionMenu
      triggerIcon={ArrowPathIcon}
      triggerIconClassName={actionIconClass}
      triggerLabel={t('community.repost.repost', 'Repost')}
      menuTitle={t('community.repost.actions', 'Repost actions')}
      items={[
        {
          key: 'repost',
          label: t('community.repost.repost', 'Repost'),
          icon: ArrowPathIcon,
          onClick: handleRepost,
          disabled: !canRepost,
        },
        {
          key: 'quote_post',
          label: t('community.repost.quote_post', 'Quote post'),
          icon: PencilSquareIcon,
          onClick: handleQuotePost,
          disabled: !canPostAnywhere,
        },
      ]}
    />
  );
};
